import {
  bundledDuaRouteDescriptors,
  type BundledDuaRouteDescriptor,
} from './route-manifest'

export const QURAN_READER_PAGE_MIN = 0
export const QURAN_READER_PAGE_MAX = 604
export const CEVSEN_READER_PAGE_MIN = 1
export const CEVSEN_READER_PAGE_MAX = 10

export type NativeReaderRoute =
  | { kind: 'quran'; page: number }
  | { kind: 'dua'; type: string; page: number }
  | { kind: 'cevsen'; page: number }

export type ReaderQueryValue = string | string[] | undefined
export type ReaderQuery = Record<string, ReaderQueryValue>

/** Resolves only canonical bundled dua slugs used by the reader shell. */
export const getBundledDuaRouteDescriptor = (
  type: string,
): BundledDuaRouteDescriptor | null =>
  bundledDuaRouteDescriptors.find((descriptor) => descriptor.slug === type) ?? null

const parsePage = (
  value: string | undefined,
  min: number,
  max: number,
): number | null => {
  if (!value || !/^\d+$/.test(value)) return null

  const page = Number(value)
  return Number.isSafeInteger(page) && page >= min && page <= max ? page : null
}

const readQueryValue = (
  query: ReaderQuery,
  key: string,
): { present: boolean; value: string | undefined } => {
  const rawValue = query[key]
  if (rawValue === undefined) return { present: false, value: undefined }
  return typeof rawValue === 'string'
    ? { present: true, value: rawValue }
    : { present: true, value: undefined }
}

/** Validates the query contract consumed by `/app-reader`. */
export const parseAppReaderQuery = (query: ReaderQuery): NativeReaderRoute | null => {
  const kind = readQueryValue(query, 'kind')
  const page = readQueryValue(query, 'page')
  const type = readQueryValue(query, 'type')

  if (!kind.present || !kind.value || !page.present || !page.value) return null
  if (type.present && !type.value) return null

  if (kind.value === 'quran') {
    if (type.present) return null
    const routePage = parsePage(page.value, QURAN_READER_PAGE_MIN, QURAN_READER_PAGE_MAX)
    return routePage === null ? null : { kind: 'quran', page: routePage }
  }

  if (kind.value === 'cevsen') {
    if (type.present) return null
    const routePage = parsePage(
      page.value,
      CEVSEN_READER_PAGE_MIN,
      CEVSEN_READER_PAGE_MAX,
    )
    return routePage === null ? null : { kind: 'cevsen', page: routePage }
  }

  if (kind.value !== 'dua' || !type.value) return null
  const descriptor = getBundledDuaRouteDescriptor(type.value)
  if (!descriptor || descriptor.view !== 'sections') return null
  const duaPage = parsePage(page.value, 1, descriptor?.pageCount ?? 0)
  return duaPage !== null ? { kind: 'dua', type: descriptor.slug, page: duaPage } : null
}

/** Resolves an explicit trusted public reader pathname to the generic shell. */
export const parseNativeReaderPath = (pathname: string): NativeReaderRoute | null => {
  if (!pathname.startsWith('/') || pathname.includes('//')) return null

  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return null

  const decodedSegments = segments.map((segment) => {
    try {
      const decoded = decodeURIComponent(segment)
      return /^[a-z0-9-]+$/i.test(decoded) ? decoded : null
    } catch {
      return null
    }
  })
  if (decodedSegments.some((segment) => !segment)) return null

  const [root, type, rawPage] = decodedSegments as string[]
  if (root === 'kuran' && segments.length === 2) {
    const page = parsePage(type, QURAN_READER_PAGE_MIN, QURAN_READER_PAGE_MAX)
    return page === null ? null : { kind: 'quran', page }
  }

  if (root === 'cevsen' && segments.length === 2) {
    const page = parsePage(type, CEVSEN_READER_PAGE_MIN, CEVSEN_READER_PAGE_MAX)
    return page === null ? null : { kind: 'cevsen', page }
  }

  if (root !== 'dualar' || !type || !rawPage || segments.length !== 3) return null
  const descriptor = getBundledDuaRouteDescriptor(type)
  const page = parsePage(rawPage, 1, descriptor?.pageCount ?? 0)
  if (!descriptor || page === null) return null
  if (descriptor.slug === 'cevsen') return { kind: 'cevsen', page }
  return { kind: 'dua', type: descriptor.slug, page }
}

/**
 * Builds the public website pathname for a reader route.
 * This is the reverse of `parseNativeReaderPath`, used when sharing from the app.
 */
export const getPublicReaderPath = (route: NativeReaderRoute): string => {
  if (route.kind === 'quran') return `/kuran/${route.page}`
  if (route.kind === 'cevsen') return `/cevsen/${route.page}`
  return `/dualar/${route.type}/${route.page}`
}

/** Builds the only URL shape used for native reader pagination and deep links. */
export const getAppReaderUrl = (route: NativeReaderRoute, hash = ''): string => {
  const params = new URLSearchParams()
  params.set('kind', route.kind)
  if (route.kind === 'dua') params.set('type', route.type)
  params.set('page', String(route.page))

  const normalizedHash = hash ? (hash.startsWith('#') ? hash : `#${hash}`) : ''
  return `/app-reader?${params.toString()}${normalizedHash}`
}

/** Returns the validated upper bound used by the shared pagination controls. */
export const getNativeReaderPageMax = (route: NativeReaderRoute): number => {
  if (route.kind === 'quran') return QURAN_READER_PAGE_MAX
  if (route.kind === 'cevsen') return CEVSEN_READER_PAGE_MAX
  return getBundledDuaRouteDescriptor(route.type)?.pageCount ?? 0
}
